import { Subwerkcat } from "@/models/subwerkcat";
import { Werkcat } from "@/models/werkcat";
import { RootState } from "@/store";
import { GetOptionLabel, createFilter } from "react-select";
import Select from "react-select";
import { WindowedMenuList } from "react-windowed-select";
import { selectStylesNoWrap } from "../bestek-filter/bestek-filter";
import { HoofdcodesState, hoofdcodesActions } from "@/store/reducers/hoofdcodes";
import { connect, useDispatch } from "react-redux";
import { Hoofdcode } from "@/models/hoofdcode";
import { useEffect, useMemo } from "react";
import apiService from "@/api-service";

interface OwnProps {
    werkcats?: Werkcat[];
    subwerkcats?: Subwerkcat[];
    selected: Hoofdcode[];
    onChange: (value: Hoofdcode[]) => void;
    usage?: 'bestek_filter' | 'hoofdcode_list';
    loading?: boolean;
}

interface Props extends OwnProps {
    hoofdcodes_state: HoofdcodesState;
}

function mapStateToProps(state: RootState, ownProps: OwnProps): Props {
    return Object.assign({
        hoofdcodes_state: state.hoofdcodes
    }, ownProps);
}

function HoofdCodeFilter(props: Props): JSX.Element {
    const dispatch = useDispatch();
    const usage = props.usage || 'bestek_filter';
    
    // CROW hergebruikt soms een codenummer in een latere jaarversie voor een compleet
    // andere betekenis. Twee rijen met hetzelfde label mogen dus alleen als "dezelfde
    // code" worden samengevoegd als hun omschrijving ook echt overeenkomt.
    const normalizeDescription = (hc: Hoofdcode): string => {
        const text = hc.omschrijving || hc.hoofdtekst || hc.romptekst || '';
        return text.trim().toLowerCase();
    };

    // Samenvoegsleutel: label + omschrijving. Jaarversies met dezelfde betekenis delen
    // dezelfde sleutel (en worden dus samengevoegd); jaarversies met een afwijkende
    // betekenis onder hetzelfde nummer krijgen elk hun eigen sleutel (blijven apart).
    //
    // 99-codes ("vrije tekst") gebruiken bewust dezelfde regel als reguliere codes, geen
    // uitzondering: hun omschrijving is altijd "Vrije tekst", dus ze vallen hier vanzelf
    // samen tot één dropdown-optie per label. Er is al een aparte Jaarversie-filter op de
    // pagina om op jaar te filteren; bij selectie van bv. "10.01.99" toont de resultatentabel
    // vanzelf elke jaarversie als eigen rij (zelfde expansie-mechanisme als reguliere codes).
    const getMergeKey = (hc: Hoofdcode): string => `${hc.label}||${normalizeDescription(hc)}`;

    // Labels waarvoor niet alle jaarversies dezelfde betekenis hebben — die moeten in de
    // dropdown met jaarversie erbij getoond worden, anders zijn ze niet te onderscheiden.
    //
    // BELANGRIJK: gememoized op de dataset, NIET opnieuw berekend per optie. react-select
    // roept getOptionLabel voor élke optie in de lijst aan — zonder memoization liep dit dus
    // voor elke van de (na vandaag flink gegroeide) duizenden hoofdcodes de hele dataset
    // opnieuw rond, wat de dropdown in de praktijk deed vastlopen.
    const ambiguousLabels = useMemo((): Set<string> => {
        const meaningsPerLabel = new Map<string, Set<string>>();
        for (const hc of props.hoofdcodes_state.data) {
            const meanings = meaningsPerLabel.get(hc.label) ?? new Set<string>();
            meanings.add(normalizeDescription(hc));
            meaningsPerLabel.set(hc.label, meanings);
        }

        const ambiguous = new Set<string>();
        meaningsPerLabel.forEach((meanings, label) => {
            if (meanings.size > 1) {
                ambiguous.add(label);
            }
        });
        return ambiguous;
    }, [props.hoofdcodes_state.data]);

    const hoofdCodeOptionLabelCallback: GetOptionLabel<Hoofdcode> = (o: Hoofdcode) => {
        const omschrijving = o.omschrijving || o.romptekst || o.hoofdtekst || 'Geen omschrijving';
        // Jaarversie alleen tonen als dit label meerdere, verschillende betekenissen heeft
        // over de jaarversies heen — anders staat één optie al voor de code als geheel.
        const jaarversieSuffix = ambiguousLabels.has(o.label) ? ` (${o.versie_jaarversie})` : '';
        return `${o.label}${jaarversieSuffix} - ${omschrijving}`;
    };

    // Eén Hoofdcode-object per samenvoegsleutel. Gebruikt om zowel de dropdown-opties als
    // de geselecteerde chips te deduplicen, zodat dezelfde (betekenis van een) code niet
    // meerdere keren identiek in beeld komt.
    const dedupeByMergeKey = (codes: Hoofdcode[]): Hoofdcode[] => {
        const byKey = new Map<string, Hoofdcode>();
        for (const hc of codes) {
            const key = getMergeKey(hc);
            if (!byKey.has(key)) {
                byKey.set(key, hc);
            }
        }
        return Array.from(byKey.values());
    };

    // Load all hoofdcodes on mount (just like werkcategorie and subwerkcategorie)
    useEffect(() => {
        if (!props.hoofdcodes_state.loaded) {
            console.log('🔄 Loading hoofdcodes via GET /hoofdcode/all...');
            
            apiService.get<Hoofdcode[]>("/hoofdcode/all")
                .then((res) => {
                    console.log(`✅ Loaded ${res.data.length} hoofdcodes for filter dropdown`);
                    dispatch(hoofdcodesActions.set_data(res.data));
                })
                .catch((error) => {
                    console.error('❌ Failed to load hoofdcodes:', error);
                });
        } else {
            console.log(`ℹ️ Hoofdcodes already loaded: ${props.hoofdcodes_state.data.length} items`);
        }
    }, [dispatch, props.hoofdcodes_state.loaded]);

    // Filter selected hoofdcodes when categories change
    useEffect(() => {
        const selectedCatIDs = (props.werkcats ?? []).map(c => c.id);
        const selectedSubcatIDs = (props.subwerkcats ?? []).map(s => s.id);

        // Filter selections based on category filters
        let filteredSet = props.selected.filter(hc => {
            // If no category filters, keep all
            if (selectedCatIDs.length === 0 && selectedSubcatIDs.length === 0) {
                return true;
            }
            
            // Match against selected categories
            const matchesWerkcat = selectedCatIDs.length === 0 || selectedCatIDs.includes(hc.werk_categorie?.id);
            const matchesSubwerkcat = selectedSubcatIDs.length === 0 || selectedSubcatIDs.includes(hc.subwerk_categorie?.id);
            return matchesWerkcat && matchesSubwerkcat;
        });

        // Update selections if they changed
        if (filteredSet.length !== props.selected.length || 
            !filteredSet.every(hc => props.selected.some(s => s.id === hc.id))) {
            setTimeout(() => {
                props.onChange([...filteredSet]);
            }, 50);
        }
    }, [props.werkcats, props.subwerkcats, props.selected, props.onChange]);

    // Gememoized om datzelfde reden als ambiguousLabels hierboven: dit filtert/dedupliceert/
    // sorteert de volledige dataset, en moet dus niet bij elke render (of erger, per optie)
    // opnieuw draaien.
    const options = useMemo((): Hoofdcode[] => {
        const selectedCatIDs = (props.werkcats ?? []).map(c => c.id);
        const selectedSubcatIDs = (props.subwerkcats ?? []).map(s => s.id);

        // Filter hoofdcodes based on selected categories
        let filteredCodes = props.hoofdcodes_state.data.filter(hc => {
            // If no category filters, show all
            if (selectedCatIDs.length === 0 && selectedSubcatIDs.length === 0) {
                return true;
            }

            // Match against selected categories
            const matchesWerkcat = selectedCatIDs.length === 0 || selectedCatIDs.includes(hc.werk_categorie?.id);
            const matchesSubwerkcat = selectedSubcatIDs.length === 0 || selectedSubcatIDs.includes(hc.subwerk_categorie?.id);
            return matchesWerkcat && matchesSubwerkcat;
        });

        // Eén optie per samenvoegsleutel: meerdere jaarversies van dezelfde code mét
        // dezelfde betekenis (bv. 72.20.02 in zowel 2020-05 als 2025-09) tonen we hier
        // maar één keer. Bij selectie worden alsnog alle onderliggende jaarversies
        // meegenomen, zie handleChange hieronder — zo toont de resultatentabel na
        // filteren wél elke jaarversie als eigen rij. Jaarversies met een afwijkende
        // betekenis onder hetzelfde label blijven wél als losse opties staan.
        let dedupedCodes = dedupeByMergeKey(filteredCodes);

        // Sort alphabetically
        try {
            dedupedCodes.sort((a, b) => {
                const labelA = a.label || '';
                const labelB = b.label || '';
                return labelA.localeCompare(labelB);
            });
        } catch (error) {
            console.error('❌ Sort failed:', error);
        }

        return dedupedCodes;
    }, [props.hoofdcodes_state.data, props.werkcats, props.subwerkcats]);

    // Wat er als selectie wordt doorgegeven aan de parent, mag best meerdere hoofdcode-ID's
    // per gekozen samenvoegsleutel bevatten (dat is nodig voor de zoekopdracht) — maar de
    // dropdown moet daarvan zelf nog steeds maar één chip per (label + betekenis) tonen.
    const selectedForDisplay = (): Hoofdcode[] => dedupeByMergeKey(props.selected);

    const handleChange = (v: readonly Hoofdcode[]) => {
        const selectedKeys = new Set(v.map(hc => getMergeKey(hc)));

        // Breid elke gekozen (label + betekenis) uit naar ál zijn hoofdcode-ID's over de
        // jaarversies heen die daadwerkelijk dezelfde betekenis delen.
        const expanded = props.hoofdcodes_state.data.filter(hc => selectedKeys.has(getMergeKey(hc)));

        // Val terug op de losse geselecteerde items zelf als ze niet in de geladen dataset
        // voorkomen (bv. virtuele 99-codes, die geen gewone hoofdcode-rij hebben).
        const knownIds = new Set(expanded.map(hc => hc.id));
        const fallback = v.filter(hc => !knownIds.has(hc.id));

        props.onChange([...expanded, ...fallback]);
    };

    const isLoading = props.loading || !props.hoofdcodes_state.loaded;

    const inputId = usage === 'hoofdcode_list' ? 'hoofdcodes-filter-select-box' : 'hoofdcodes-select-box';
    const placeholder = usage === 'hoofdcode_list' ? 'Selecteer hoofdcodes...' : undefined;

    return (
        <>
            <label htmlFor={inputId}>Hoofdcodes</label>
            <Select
                styles={selectStylesNoWrap}
                components={{ MenuList: WindowedMenuList }}
                inputId={inputId}
                options={options}
                value={selectedForDisplay()}
                getOptionLabel={hoofdCodeOptionLabelCallback}
                getOptionValue={(o) => getMergeKey(o)}
                onChange={handleChange}
                isLoading={isLoading}
                filterOption={createFilter({ignoreAccents: false})}
                placeholder={placeholder}
                isMulti
                isClearable
            />
        </>
    );
}

export default connect(mapStateToProps)(HoofdCodeFilter);